import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import QRCode from 'qrcode';
import type { MfaEnrollResponse } from '@hesabat/contracts';
import { InvalidCodeError, MfaPendingError, api } from './api';
import Hint from './Hint';

interface MfaEnrollProps {
  onEnrolled: () => void;
  onCancel: () => void;
  /** التفعيل من «حسابى» باختيار المستخدم، لا فرض الدور — يغيّر النص وحده. */
  voluntary?: boolean;
}

/**
 * شاشة تسجيل العامل الثانى الإجبارى — عربية RTL.
 *
 * الرمز المربع يُرسم **فى المتصفح** من otpauth URI، فلا تُنتج الخدمة صورة
 * تحمل السر ولا تمر عبر وسيط أو سجل. وأكواد الاسترداد تُعرض مرة واحدة: لا
 * مسار يقرؤها بعد هذه الاستجابة، فمن يغلق الشاشة قبل حفظها يفقدها.
 */
export default function MfaEnroll({ onEnrolled, onCancel, voluntary = false }: MfaEnrollProps) {
  const [enrollment, setEnrollment] = useState<MfaEnrollResponse | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const start = useCallback(async () => {
    try {
      const started = await api.mfaEnroll();
      setEnrollment(started);
      setQr(await QRCode.toDataURL(started.otpauthUri, { margin: 1, width: 220 }));
    } catch (caught) {
      setError(
        caught instanceof MfaPendingError
          ? 'انتهت مهلة التسجيل. سجّل الدخول من جديد.'
          : 'تعذر بدء التسجيل. تأكد أن الـ API يعمل.',
      );
    }
  }, []);

  // بدء واحد فقط: بدءان متزامنان (StrictMode يشغّل الأثر مرتين) يتسابقان على نفس الصف
  // فيفشل التانى، وتظهر رسالة خطأ فوق رمز سليم
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void start();
  }, [start]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const confirmed = await api.mfaConfirm(code.trim());
      // السر يغادر حالة المكوّن فور التأكيد — لا يبقى فى الذاكرة بلا داعٍ
      setEnrollment(null);
      setQr(null);
      setCode('');
      setRecoveryCodes(confirmed.recoveryCodes);
    } catch (caught) {
      setError(
        caught instanceof InvalidCodeError
          ? 'الرمز غير صحيح. تأكد من الرمز الحالى فى التطبيق.'
          : 'تعذر التأكيد. أعد المحاولة.',
      );
      setCode('');
    } finally {
      setSubmitting(false);
    }
  }

  if (recoveryCodes) {
    return (
      <div className="login-screen">
        <div className="login-card">
          <div className="logo">
            حسابات<span> • </span>نواة
          </div>
          <h1>أكواد الاسترداد</h1>
          <p className="sub">
            احفظها الآن فى مكان آمن. <strong>لن تُعرض مرة أخرى</strong>، وكل كود يُستخدم مرة واحدة
            فقط عند فقد الهاتف.
          </p>

          <ul className="recovery-codes" dir="ltr">
            {recoveryCodes.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>

          <button type="button" className="primary" onClick={onEnrolled}>
            حفظتها — تابع
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-screen">
      <form className="login-card" onSubmit={handleSubmit}>
        <div className="logo">
          حسابات<span> • </span>نواة
        </div>
        <h1>تسجيل التحقق بخطوتين</h1>
        <p className="sub">
          {voluntary ? 'امسح الرمز' : 'دورك يستوجب عاملاً ثانيًا. امسح الرمز'} بتطبيق مصادقة، ثم
          اكتب الرمز الظاهر لتأكيد التسجيل.
        </p>

        {qr && <img className="mfa-qr" src={qr} alt="رمز تسجيل تطبيق المصادقة" />}

        {enrollment && (
          <p className="sub mfa-secret" dir="ltr">
            {enrollment.secret}
          </p>
        )}

        <label htmlFor="code">
          الرمز من التطبيق
          <Hint>الأرقام الستة الظاهرة الآن فى تطبيق المصادقة على موبايلك — تتغير كل ٣٠ ثانية.</Hint>
        </label>
        <input
          id="code"
          name="code"
          type="text"
          dir="ltr"
          inputMode="numeric"
          autoComplete="one-time-code"
          required
          minLength={6}
          maxLength={6}
          value={code}
          onChange={(event) => setCode(event.target.value)}
        />

        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}

        <button type="submit" className="primary" disabled={submitting || !enrollment}>
          {submitting ? 'جارى التأكيد…' : 'تأكيد التسجيل'}
        </button>

        <button type="button" className="ghost" onClick={onCancel} disabled={submitting}>
          {voluntary ? 'رجوع' : 'إلغاء والخروج'}
        </button>

        <p className="sub login-hint">
          السر أعلاه لمن يُدخله يدويًا بدل المسح. لا يُعاد عرضه بعد التأكيد.
        </p>
      </form>
    </div>
  );
}
